import axios from 'axios';
import { optimizeImageForUpload } from './utils/imageUpload';
import { FILE_LIMITS, formatBytes } from './utils/tripValidation';
import { getUserFacingErrorMessage, isTechnicalErrorMessage } from './utils/errorMessage';

// =============================================
// API Configuration
// =============================================

// Em producao, o baseURL aponta para https://globememories.com/api
// O Nginx faz rewrite de /api/X para /X antes de mandar para o backend.
// Em desenvolvimento, o baseURL aponta para http://localhost:8080 (sem /api).
const isProduction = process.env.REACT_APP_ENV === 'production' || (typeof window !== 'undefined' && window.location.protocol === 'https:');
const API_BASE_URL = isProduction
  ? ((process.env.REACT_APP_API_URL || 'https://globememories.com') + '/api')
  : (process.env.REACT_APP_API_URL || 'http://localhost:8080');
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT || '10000');
// Video uploads can legitimately take longer than ordinary JSON API calls,
// especially on mobile networks and when the backend prepares an MP4 for
// streaming. The server keeps the upload alive; do not let Axios abort it
// after the general 10-second API timeout.
const VIDEO_UPLOAD_TIMEOUT = 15 * 60 * 1000;

// Storage keys for different token spaces
export const STORAGE_KEYS = {
  USER: 'auth_token',
  ADMIN: 'adminToken',
  USER_DATA: 'user',
  ADMIN_DATA: 'adminUser',
};

// =============================================
// Token Management
// =============================================

export const getAuthToken = (key = STORAGE_KEYS.USER) => {
  if (typeof window === 'undefined') return null;
  // A normal login is scoped to the current tab, while "Lembrar-me"
  // deliberately survives browser restarts in localStorage. Keep the
  // lookup in one place so every API request supports both modes.
  const token = window.localStorage.getItem(key)
    || (key === STORAGE_KEYS.USER ? window.sessionStorage.getItem(key) : null);
  return token && token !== 'null' && token !== 'undefined' ? token : null;
};

export const setAuthHeader = (token, key = STORAGE_KEYS.USER, { persistent = true } = {}) => {
  if (typeof window === 'undefined') return;
  if (token) {
    const targetStorage = persistent ? window.localStorage : window.sessionStorage;
    const otherStorage = persistent ? window.sessionStorage : window.localStorage;
    targetStorage.setItem(key, token);
    otherStorage.removeItem(key);
  } else {
    window.localStorage.removeItem(key);
    if (key === STORAGE_KEYS.USER) {
      window.sessionStorage.removeItem(key);
      window.localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      window.sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
    } else if (key === STORAGE_KEYS.ADMIN) {
      window.localStorage.removeItem(STORAGE_KEYS.ADMIN_DATA);
    }
  }
};

export const clearAuthToken = (key = STORAGE_KEYS.USER) => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(key);
  if (key === STORAGE_KEYS.USER) window.sessionStorage.removeItem(key);
  if (key === STORAGE_KEYS.USER) {
    window.localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    window.sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
  } else if (key === STORAGE_KEYS.ADMIN) {
    window.localStorage.removeItem(STORAGE_KEYS.ADMIN_DATA);
  }
};

/**
 * Persist the authenticated user in the same storage as its token. A
 * persistent session is intentionally indefinite from the browser's point of
 * view: it ends on explicit logout, account removal/ban, or browser storage
 * being cleared. The API controls whether the accompanying token is
 * persistent as well.
 */
export const persistAuthSession = (userData, { persistent = true } = {}) => {
  if (typeof window === 'undefined' || !userData?.token) return;
  setAuthHeader(userData.token, STORAGE_KEYS.USER, { persistent });
  const targetStorage = persistent ? window.localStorage : window.sessionStorage;
  const otherStorage = persistent ? window.sessionStorage : window.localStorage;
  targetStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
  otherStorage.removeItem(STORAGE_KEYS.USER_DATA);
};

export const clearAllAuth = () => {
  clearAuthToken(STORAGE_KEYS.USER);
  clearAuthToken(STORAGE_KEYS.ADMIN);
};

// =============================================
// Decode JWT (lightweight, no validation)
// =============================================
// Note: this only decodes; signature is verified server-side.

export const decodeJwt = (token) => {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch (e) {
    return null;
  }
};

export const getRole = (key = STORAGE_KEYS.USER) => {
  const token = getAuthToken(key);
  const decoded = decodeJwt(token);
  return decoded?.role || decoded?.['https://globe-memories/role'] || null;
};

// =============================================
// Axios Instance
// =============================================

// The API intentionally disables browser caching for authenticated responses.
// That is the right security default, but it used to mean that every route
// change in the PWA fetched the same countries, catalogues, profile data and
// feed pages again. Safari/iOS is particularly sensitive to those extra
// round-trips because a standalone PWA often has a colder connection than
// Chrome on Android. Keep a small, user-scoped read cache here instead of
// caching personalised API responses in the service worker.
const READ_CACHE_STORAGE_KEY = 'gm:api-read-cache:v3';
const readCache = new Map();
const readInFlight = new Map();
let readCacheGeneration = 0;

const REFERENCE_PATHS = new Set([
  '/categories',
  '/languages-spoken',
  '/accommodation-types',
  '/accommodation-boards',
  '/transports',
  '/reference-point-types',
]);

const getRelativePath = (url) => {
  const raw = String(url || '').split('?')[0];
  if (/^https?:\/\//i.test(raw)) {
    try {
      return new URL(raw).pathname.replace(/^\/api(?=\/)/, '') || '/';
    } catch (_) {
      return raw.replace(/^https?:\/\/[^/]+/i, '').replace(/^\/api(?=\/)/, '') || '/';
    }
  }
  return (raw.startsWith('/') ? raw : `/${raw}`).replace(/^\/api(?=\/)/, '') || '/';
};

// Shared profile links must remain readable even when a browser still has an
// expired JWT in storage. Do not attach that stale token to the anonymous
// lookup requests; otherwise the backend's JWT filter can turn a public read
// into a 401 before the controller gets a chance to apply its privacy rules.
const isPublicProfileRead = (url) => {
  const path = getRelativePath(url);
  return path === '/users/search'
    || /^\/users\/\d+\/detailed$/.test(path)
    || /^\/trips\/user\/\d+\/public$/.test(path);
};

// Keep query parameters in read-cache keys. Several discovery screens build
// GET URLs with the query string inline (for example `/trips/public-feed?...`)
// instead of passing `config.params`; dropping that string made every filter
// combination share the same cached response.
const getReadCacheQuery = (url) => {
  const raw = String(url || '');
  const queryStart = raw.indexOf('?');
  if (queryStart < 0) return '';
  const hashStart = raw.indexOf('#', queryStart);
  return raw.slice(queryStart + 1, hashStart < 0 ? raw.length : hashStart);
};

const getReadCachePolicy = (url) => {
  const path = getRelativePath(url);
  if (REFERENCE_PATHS.has(path)) {
    // These lists are managed from the backoffice. A catalogue update must
    // be visible on the next page load, including in another browser/device,
    // so never reuse a stale localStorage or in-memory response here.
    return null;
  }

  // These responses are scoped to the current user because they include
  // flags such as isLiked/isSaved. They stay in memory only and are cleared
  // after mutations, so navigation is fast without leaking data between
  // accounts or browser sessions.
  if (/^\/trips\/(public-feed|following-feed)$/.test(path)
    || /^\/trips\/user\/\d+\/public$/.test(path)) {
    return { ttlMs: 15 * 1000, maxStaleMs: 90 * 1000, persistent: false, public: false };
  }
  if (/^\/trips\/\d+(\/comments)?$/.test(path) || /^\/users\/\d+\/detailed$/.test(path)
    || /^\/users\/(discover|search)$/.test(path)) {
    return { ttlMs: 20 * 1000, maxStaleMs: 90 * 1000, persistent: false, public: false };
  }
  return null;
};

const stableValue = (value) => {
  if (value === undefined) return null;
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(stableValue);
  if (typeof FormData !== 'undefined' && value instanceof FormData) return '[form-data]';
  return Object.keys(value).sort().reduce((result, key) => {
    result[key] = stableValue(value[key]);
    return result;
  }, {});
};

const getReadCacheScope = () => {
  try {
    const stored = typeof window !== 'undefined'
      ? (window.localStorage.getItem(STORAGE_KEYS.USER_DATA)
        || window.sessionStorage.getItem(STORAGE_KEYS.USER_DATA))
      : null;
    const user = stored ? JSON.parse(stored) : null;
    if (user?.id != null) return `user:${user.id}`;
  } catch (_) {
    // Storage can be unavailable in Safari private browsing. Memory caching
    // still works for the current page in that case.
  }
  const token = getAuthToken(STORAGE_KEYS.USER);
  const subject = decodeJwt(token)?.sub;
  return subject ? `subject:${subject}` : 'anonymous';
};

const getReadCacheKey = (config, policy) => {
  const path = getRelativePath(config?.url);
  const viewerSensitiveProfileRead = isPublicProfileRead(config?.url);
  // These endpoints can return different payloads for anonymous visitors,
  // followers and the profile owner. Keep the anonymous response in its own
  // cache namespace so it can never hide owner-only statistics from an
  // authenticated profile request for the same user/URL.
  const scope = policy.public
    ? 'public'
    : viewerSensitiveProfileRead
      ? (config?.withPublicProfileAuth === true
        ? `authenticated:${getReadCacheScope()}`
        : 'anonymous')
      : getReadCacheScope();
  const urlQuery = getReadCacheQuery(config?.url);
  const params = stableValue(config?.params);
  return `${scope}|${path}|${urlQuery}|${JSON.stringify(params)}`;
};

const restorePersistentReadCache = () => {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(READ_CACHE_STORAGE_KEY);
    if (!raw) return;
    const stored = JSON.parse(raw);
    const now = Date.now();
    Object.entries(stored || {}).forEach(([key, entry]) => {
      if (!entry || !entry.persistent || !entry.storedAt
        || now - entry.storedAt > 24 * 60 * 60 * 1000) return;
      readCache.set(key, entry);
    });
  } catch (_) {
    // Ignore malformed/blocked storage; the network path remains available.
  }
};

const persistReadCache = () => {
  if (typeof window === 'undefined') return;
  try {
    const serializable = {};
    [...readCache.entries()]
      .filter(([, entry]) => entry?.persistent)
      .sort(([, a], [, b]) => (b.storedAt || 0) - (a.storedAt || 0))
      .slice(0, 40)
      .forEach(([key, entry]) => {
        serializable[key] = {
          data: entry.data,
          status: entry.status,
          storedAt: entry.storedAt,
          persistent: true,
        };
      });
    window.localStorage.setItem(READ_CACHE_STORAGE_KEY, JSON.stringify(serializable));
  } catch (_) {
    // Quota/private-mode failures must never break an API request.
  }
};

const storeReadCacheEntry = (key, response, policy, generation) => {
  if (generation !== readCacheGeneration || !response) return;
  const entry = {
    data: response.data,
    status: response.status,
    storedAt: Date.now(),
    persistent: policy.persistent,
  };
  readCache.set(key, entry);
  // Keep memory bounded if a search/feed page has many query combinations.
  while (readCache.size > 80) {
    const oldestKey = readCache.keys().next().value;
    if (oldestKey === undefined) break;
    readCache.delete(oldestKey);
  }
  if (policy.persistent) persistReadCache();
};

const responseFromReadCache = (entry, config) => ({
  data: entry.data,
  status: entry.status || 200,
  statusText: 'OK',
  headers: {},
  config,
  request: undefined,
  fromReadCache: true,
});

const refreshReadCache = (key, config, policy, sendRequest) => {
  const canDeduplicate = !config?.signal;
  if (canDeduplicate && readInFlight.has(key)) return readInFlight.get(key);

  const generation = readCacheGeneration;
  const promise = Promise.resolve()
    .then(sendRequest)
    .then((response) => {
      storeReadCacheEntry(key, response, policy, generation);
      return response;
    })
    .finally(() => {
      if (canDeduplicate) readInFlight.delete(key);
    });

  if (canDeduplicate) readInFlight.set(key, promise);
  return promise;
};

const performCachedGet = (config, sendRequest) => {
  const policy = getReadCachePolicy(config?.url);
  const requestedCache = String(config?.cache || '').toLowerCase();
  if (!policy || requestedCache === 'no-store' || config?.noReadCache) return sendRequest();

  const key = getReadCacheKey(config, policy);
  const entry = readCache.get(key);
  const age = entry ? Date.now() - entry.storedAt : Number.POSITIVE_INFINITY;

  if (entry && age <= policy.ttlMs) {
    return Promise.resolve(responseFromReadCache(entry, config));
  }

  if (entry && age <= policy.maxStaleMs) {
    // Stale-while-revalidate: render the last known value immediately and
    // refresh it in the background. This removes the visible Safari delay
    // when returning to Home, Travels, a profile or a comments sheet.
    refreshReadCache(key, config, policy, sendRequest).catch(() => {});
    return Promise.resolve(responseFromReadCache(entry, config));
  }

  return refreshReadCache(key, config, policy, sendRequest);
};

export const clearReadCache = ({ includeReference = true } = {}) => {
  readCacheGeneration += 1;
  [...readCache.entries()].forEach(([key, entry]) => {
    if (includeReference || !entry?.persistent) readCache.delete(key);
  });
  if (includeReference && typeof window !== 'undefined') {
    try { window.localStorage.removeItem(READ_CACHE_STORAGE_KEY); } catch (_) { /* ignore */ }
  } else if (typeof window !== 'undefined') {
    persistReadCache();
  }
};

restorePersistentReadCache();

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// =============================================
// Request Interceptor
// =============================================

axiosInstance.interceptors.request.use(
  (config) => {
    const method = String(config.method || 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD') {
      const path = getRelativePath(config.url);
      const changesReferenceData = path.startsWith('/admin/')
        || /^\/(cities|categories|languages-spoken|accommodation-types|accommodation-boards|transports|reference-point-types)(\/|$)/.test(path);
      clearReadCache({ includeReference: changesReferenceData });
    }

    // Pick the right token based on the URL path
    const isAdminRequest = typeof config.url === 'string' && config.url.includes('/admin/');
    const key = isAdminRequest ? STORAGE_KEYS.ADMIN : STORAGE_KEYS.USER;
    const token = getAuthToken(key);
    // Public profile reads stay anonymous by default so a stale token cannot
    // break a shared public link. Authenticated profile viewers explicitly
    // opt into the token via `withPublicProfileAuth`; this is required for
    // private profiles where following/ownership determines visibility.
    const withPublicProfileAuth = config.withPublicProfileAuth === true;
    if (token && (!(method === 'GET' && isPublicProfileRead(config.url)) || withPublicProfileAuth)) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// =============================================
// Response Interceptor
// =============================================

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const isAdminRequest = typeof url === 'string' && url.includes('/admin/');
    const isPublicProfileRequest = !isAdminRequest
      && isPublicProfileRead(url)
      && error.config?.withPublicProfileAuth !== true;

    // Keep one safe copy available to UI callers. We deliberately do not
    // replace Axios' error.message because auth/network flows use it for
    // control flow; components must use getUserFacingErrorMessage instead.
    const serverMessage = error.response?.data?.message;
    if ((typeof status === 'number' && status >= 500) || isTechnicalErrorMessage(serverMessage)) {
      const userMessage = getUserFacingErrorMessage(error);
      error.userMessage = userMessage;
      // Most screens consume response.data.message directly. Replace only
      // technical/5xx payloads so those existing callers cannot leak backend
      // details; normal business messages remain untouched.
      if (error.response) {
        if (error.response.data && typeof error.response.data === 'object') {
          error.response.data = { ...error.response.data, message: userMessage };
        } else {
          error.response.data = { message: userMessage };
        }
      }
    }

    if (status === 401) {
      // A public profile is not an authentication failure. If an older
      // deployment or a proxy still answers 401 for one of these reads,
      // never bounce the visitor to the login page just because the browser
      // has a stale session token.
      if (isPublicProfileRequest) {
        return Promise.reject(error);
      }
      clearReadCache({ includeReference: false });
      // Token invalid or expired for the current namespace
      if (typeof window !== 'undefined') {
        if (isAdminRequest) {
          clearAuthToken(STORAGE_KEYS.ADMIN);
          // Don't redirect the user app if the failure was on the admin namespace
          if (window.location.pathname.startsWith('/admin')) {
            window.location.href = '/admin/login';
          }
        } else {
          clearAuthToken(STORAGE_KEYS.USER);
          if (!window.location.pathname.startsWith('/admin')) {
            window.location.href = '/login';
          }
        }
        window.dispatchEvent(new CustomEvent('auth:logout', { detail: { isAdmin: isAdminRequest } }));
      }
    }

    if (status === 403 && process.env.NODE_ENV === 'development') {
      console.error('Acesso negado:', error.response.data);
    }

    // Round 57 — the JwtAuthFilter rejects banned users with 403 +
    // { code: "ACCOUNT_BANNED", message: "Conta suspensa: <reason>" }.
    // Round 58 — also handles ACCOUNT_DELETED when an admin removed
    // the account while the user still holds a valid token.
    // When an already-logged-in user gets banned mid-session, we want
    // to (1) log them out of the right namespace, (2) surface a toast
    // so they understand what happened, and (3) redirect to /login.
    const data = error.response?.data || {};
    if (status === 403 && (data.code === 'ACCOUNT_BANNED' || data.code === 'ACCOUNT_DELETED')) {
      const reason = data.code === 'ACCOUNT_DELETED' ? 'deleted' : 'banned';
      if (typeof window !== 'undefined') {
        if (isAdminRequest) {
          clearAuthToken(STORAGE_KEYS.ADMIN);
          if (window.location.pathname.startsWith('/admin')) {
            window.location.href = '/admin/login';
          }
        } else {
          clearAuthToken(STORAGE_KEYS.USER);
          if (!window.location.pathname.startsWith('/admin')) {
            window.location.href = '/login';
          }
        }
        window.dispatchEvent(new CustomEvent('auth:logout', {
          detail: { isAdmin: isAdminRequest, reason, message: data.message },
        }));
      }
    }

    if (status === 500 && process.env.NODE_ENV === 'development') {
      console.error('Erro no servidor:', error.response.data);
    }

    return Promise.reject(error);
  }
);

// A large part of the application uses `api.get(...)` directly while older
// screens use the request helper below. Wrap both paths so all pages share the
// same cache and in-flight request deduplication.
const rawAxiosGet = axiosInstance.get.bind(axiosInstance);
axiosInstance.get = (url, config = {}) => performCachedGet(
  { ...config, method: 'GET', url },
  () => rawAxiosGet(url, config),
);

// =============================================
// Request Helper
// =============================================

/**
 * Make an authenticated request. Supports `params` for query strings and `signal`
 * for cancellation via AbortController.
 */
export const request = (method, url, data, options = {}) => {
  const { params, signal, headers, timeout, cache, noReadCache } = options;
  const config = {
    method,
    url,
    headers: { ...(headers || {}) },
  };
  if (params) config.params = params;
  if (signal) config.signal = signal;
  if (timeout) config.timeout = timeout;
  if (cache) config.cache = cache;
  if (noReadCache) config.noReadCache = true;
  if (data !== undefined) config.data = data;
  if (String(method).toUpperCase() === 'GET') {
    return performCachedGet(config, () => axiosInstance(config));
  }
  return axiosInstance(config);
};

// =============================================
// Media / Files Utilities
// =============================================
// Re-exported from utils/mediaUrl so the test file can import them
// without dragging in axios (which has ESM syntax CRA's Jest setup
// doesn't transform by default).
export { BASE_FILES_URL, toFullMediaUrl, getUserAvatar } from './utils/mediaUrl';

/**
 * Upload a single file to a backend media endpoint using multipart/form-data.
 * The field name is "file" as required by all media upload endpoints.
 *
 * IMPORTANT: We must NOT send `Content-Type: application/json` (the default
 * set on `axiosInstance`). If we let that default through, axios serialises
 * the FormData as JSON, Spring sees no `multipart/form-data` boundary, and
 * throws `MultipartException: Current request is not a multipart request`.
 *
 * Setting `Content-Type: undefined` inside `headers` is not enough because
 * axios merges the request headers over the instance defaults — `undefined`
 * is treated as "do not override", not as "delete this header". So we
 * strip it via `transformRequest`, and we ALSO delete it from the
 * post-merge `config.headers` so the request interceptor (which runs
 * after the merge) cannot re-add it. The browser then sets the
 * `multipart/form-data; boundary=…` header itself.
 */
export const uploadFile = async (url, file, onUploadProgress, extraFields, options = {}) => {
  const lowerUrl = String(url || '').toLowerCase();
  const isVideoUpload = String(file?.type || '').toLowerCase().startsWith('video/')
    || /\.(mp4|webm|mov|avi|mkv)$/i.test(String(file?.name || ''));
  const maxUploadBytes = isVideoUpload
    ? FILE_LIMITS.maxVideoSizeBytes
    : FILE_LIMITS.maxImageSizeBytes;
  if (file && Number.isFinite(file.size) && file.size > maxUploadBytes) {
    const label = isVideoUpload ? 'Vídeo' : 'Imagem';
    throw new Error(`${label} demasiado grande (${formatBytes(file.size)}). Máximo: ${formatBytes(maxUploadBytes)}.`);
  }
  const preset = lowerUrl.includes('cover-upload')
    ? 'cover'
    : lowerUrl.includes('/photos/upload')
      ? 'profile'
      : lowerUrl.includes('/media/photos') || lowerUrl.includes('/staged/photos')
        ? 'trip'
        : 'default';
  const preparedFile = options.skipImageOptimization
    ? file
    : await optimizeImageForUpload(file, preset);
  const form = new FormData();
  form.append('file', preparedFile, preparedFile?.name || 'upload.jpg');
  // Round 62 — The wizard now sends a per-photo `caption` together
  // with the file so the backend can persist the caption in the
  // same transaction as the photo (instead of dropping it because
  // the POST /trips only carries the placeholder photo path). The
  // extra fields are appended as plain strings — Spring binds them
  // to @RequestParam on the TripMediaController.uploadTripPhoto
  // method. We only append non-blank values to avoid sending an
  // explicit empty `caption=` parameter (Spring would still accept
  // it, but a missing param triggers the `required = false` branch
  // more cleanly).
  if (extraFields && typeof extraFields === 'object') {
    for (const [key, value] of Object.entries(extraFields)) {
      if (value == null) continue;
      const str = String(value);
      if (!str) continue;
      form.append(key, str);
    }
  }
  return axiosInstance({
    method: 'POST',
    url,
    data: form,
    onUploadProgress,
    timeout: isVideoUpload ? VIDEO_UPLOAD_TIMEOUT : API_TIMEOUT,
    transformRequest: (data, headers) => {
      if (headers) {
        delete headers['Content-Type'];
        delete headers['content-type'];
      }
      return data;
    },
  });
};

/**
 * Send one raw binary part of a resumable media upload. Keeping this helper
 * next to uploadFile ensures the same JWT selection and timeout behaviour,
 * while explicitly replacing the JSON default with application/octet-stream.
 */
export const uploadChunk = async (url, blob, offset, length, onUploadProgress) => axiosInstance({
  method: 'PUT',
  url,
  params: { offset, length },
  data: blob,
  onUploadProgress,
  timeout: VIDEO_UPLOAD_TIMEOUT,
  headers: { 'Content-Type': 'application/octet-stream' },
  transformRequest: (data, headers) => {
    if (headers) {
      headers['Content-Type'] = 'application/octet-stream';
      delete headers['content-type'];
    }
    return data;
  },
});

export default axiosInstance;
